import { useDisabledWidget } from '../../../../shared/disabled-widget/useDisabledWidget';
import { WithDisabledControl } from '../../../../shared/disabled-widget/WithDisabledControl';
import { Box, Button, FormField, Text } from '@wix/design-system';
import localeDataset from '@wix/locale-dataset-javascript';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js/min';
import React, { useCallback, useMemo, useState } from 'react';
import { CountryCode, PhoneInput } from './components';
import { isCountryBlocked } from './utils';
import s from './CCPhoneInputWidget.scss';
const FALLBACK_COUNTRY_CODE = 'US';
export const CCPhoneInputWidget = _ref => {
  let {
    context,
    config
  } = _ref;
  const {
    onWidgetAction,
    send
  } = context;
  const {
    areControlsDisabled,
    enableControls
  } = useDisabledWidget(config);
  const [selectedCountry, setSelectedCountry] = React.useState(localeDataset.getCountryByShortKey(FALLBACK_COUNTRY_CODE));
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDialCodeFocused, setIsDialCodeFocused] = useState(false);
  const selectCountryHandler = useCallback(country => {
    setSelectedCountry(country);
  }, []);
  const isBlocked = useMemo(() => {
    return isCountryBlocked(selectedCountry == null ? void 0 : selectedCountry.shortKey);
  }, [selectedCountry]);
  const countryCodeErrorMessage = useMemo(() => {
    if (!isDialCodeFocused && !selectedCountry) {
      return 'Select a country code.';
    } else if (isBlocked) {
      return 'Calls are not available in this location.';
    }
    return undefined;
  }, [isDialCodeFocused, selectedCountry, isBlocked]);
  const phoneErrorMessage = useMemo(() => {
    if (selectedCountry && !isValidPhoneNumber(`${selectedCountry.dialCode}${localPhoneNumber}`)) {
      return 'Enter a number that’s 7 to 13 digits long.';
    }
    return undefined;
  }, [localPhoneNumber, selectedCountry]);
  const isSubmitButtonEnabled = Boolean(!countryCodeErrorMessage && localPhoneNumber);
  const submitHandler = useCallback(event => {
    event.preventDefault();
    setIsSubmitted(true);
    const isValid = !countryCodeErrorMessage && !phoneErrorMessage;
    if (isValid) {
      const dialCode = selectedCountry == null ? void 0 : selectedCountry.dialCode;
      const fullNumber = `${dialCode}${localPhoneNumber}`;
      const parsedNumber = parsePhoneNumber(fullNumber);
      const formattedNumber = parsedNumber.formatInternational();
      onWidgetAction(CC_PHONE_INPUT_WIDGET, {
        action: 'phoneNumberSubmitted',
        value: formattedNumber
      });
      send(formattedNumber);
    }
  }, [countryCodeErrorMessage, localPhoneNumber, selectedCountry, phoneErrorMessage, onWidgetAction, send]);
  const onDialCodeFocus = useCallback(() => {
    setIsDialCodeFocused(true);
  }, []);
  const onDialCodeBlur = useCallback(() => {
    setIsDialCodeFocused(false);
  }, []);
  const status = countryCodeErrorMessage || isSubmitted && phoneErrorMessage ? 'error' : undefined;
  const statusMessage = useMemo(() => {
    return countryCodeErrorMessage || isSubmitted && phoneErrorMessage;
  }, [countryCodeErrorMessage, isSubmitted, phoneErrorMessage]);
  const onPrivacylPolicyClick = useCallback(() => {
    onWidgetAction(CC_PHONE_INPUT_WIDGET, {
      action: 'privacyPolicyLinkClicked'
    });
  }, [onWidgetAction]);
  return /*#__PURE__*/React.createElement("form", {
    onSubmit: submitHandler,
    className: s.phoneInputForm
  }, /*#__PURE__*/React.createElement("input", {
    type: "submit",
    hidden: true
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP3",
    marginBottom: "SP1"
  }, /*#__PURE__*/React.createElement(FormField, {
    status: status
    // label="Phone number"
    // intentionaly not use to have different size for label
    ,
    statusMessage: statusMessage,
    labelSize: "tiny"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Box, {
    marginBottom: "9px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "thin"
  }, "Phone number")), /*#__PURE__*/React.createElement(Box, {
    gap: 1
  }, /*#__PURE__*/React.createElement(Box, {
    maxWidth: "80px"
  }, /*#__PURE__*/React.createElement(CountryCode, {
    selectedCountry: selectedCountry,
    onSelectCountry: selectCountryHandler,
    onFocus: onDialCodeFocus,
    onBlur: onDialCodeBlur
  })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(PhoneInput, {
    selectedCountry: selectedCountry,
    value: localPhoneNumber,
    onChange: setLocalPhoneNumber
  })))))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP3",
    marginBottom: "SP3"
  }, !statusMessage && /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin"
  }, "Enter the country code or search by country name. For the rest of the phone number, enter digits only.")), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin"
  }, "By submitting your phone number you acknowledge that you read our \xA0", /*#__PURE__*/React.createElement("a", {
    href: "https://www.wix.com/about/privacy",
    target: "_blank",
    rel: "noreferrer",
    onClick: onPrivacylPolicyClick
  }, "Privacy Policy"), "."))), /*#__PURE__*/React.createElement(Box, {
    align: "right"
  }, /*#__PURE__*/React.createElement(WithDisabledControl, {
    isDisabled: areControlsDisabled,
    onEnable: enableControls,
    t: context.t
  }, /*#__PURE__*/React.createElement(Button, {
    size: "tiny",
    disabled: !isSubmitButtonEnabled || areControlsDisabled,
    onClick: submitHandler
  }, "Send")))));
};
export const CC_PHONE_INPUT_WIDGET = 'CC_PHONE_INPUT_WIDGET';
//# sourceMappingURL=CCPhoneInputWidget.js.map